import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Save, Image as ImageIcon, Trash2, ChevronLeft, Eye, Search, Upload } from "lucide-react";
import { api, fileUrl } from "../../api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import MarkdownView from "../../components/MarkdownView";
import { useToast } from "../../hooks/use-toast";

const EMPTY = {
  slug: "", title: "", excerpt: "", body: "", cover: "", author: "Anna", date: "",
  tags: [], published: true,
  seo: { title: "", description: "", keywords: "", ogImage: "", canonical: "", noindex: false, jsonLd: "" },
};

export default function PostEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [post, setPost] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/blog/id/${id}`);
        setPost({ ...EMPTY, ...data, seo: { ...EMPTY.seo, ...(data.seo || {}) } });
      } catch { toast({ title: "Failed to load" }); }
      finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const onChange = (k, v) => setPost((p) => ({ ...p, [k]: v }));
  const onSeo = (k, v) => setPost((p) => ({ ...p, seo: { ...p.seo, [k]: v } }));

  const onUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(field, data.url);
      toast({ title: "Image uploaded" });
    } catch { toast({ title: "Upload failed" }); }
  };

  const save = async () => {
    if (!post.title) { toast({ title: "Title required" }); return; }
    setSaving(true);
    try {
      const payload = { ...post };
      if (isNew) {
        const { data } = await api.post("/admin/blog", payload);
        toast({ title: "Post created" });
        navigate(`/admin/posts/${data.id}`);
      } else {
        await api.put(`/admin/blog/${id}`, payload);
        toast({ title: "Post saved" });
      }
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400">Loading post...</div>;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/posts" className="text-sm text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back to posts</Link>
          <h1 className="text-2xl md:text-3xl font-bold mt-1">{isNew ? "New Post" : "Edit Post"}</h1>
        </div>
        <div className="flex gap-2">
          {!isNew && post.slug && <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="btn-outline-gold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1.5"><Eye className="w-4 h-4" /> Preview</a>}
          <Button onClick={save} disabled={saving} className="btn-gold border-0 px-5 py-2.5 rounded-lg"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save"}</Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="mt-6">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-5 space-y-5">
          <div className="grid lg:grid-cols-[1fr_320px] gap-5">
            <div className="card-dark p-5 space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={post.title} onChange={(e) => onChange("title", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={post.slug} onChange={(e) => onChange("slug", e.target.value)} placeholder="auto-generated from title" className="mt-1.5 bg-[#0c1530] border-white/10 text-white font-mono text-sm" />
              </div>
              <div>
                <Label>Excerpt</Label>
                <Textarea rows={2} value={post.excerpt} onChange={(e) => onChange("excerpt", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              </div>
              <div>
                <Label>Body (Markdown)</Label>
                <Textarea rows={18} value={post.body} onChange={(e) => onChange("body", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white font-mono text-sm" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="card-dark p-5">
                <Label>Published</Label>
                <div className="mt-2 flex items-center gap-2">
                  <Switch checked={post.published} onCheckedChange={(v) => onChange("published", v)} />
                  <span className="text-sm text-slate-300">{post.published ? "Live" : "Draft"}</span>
                </div>
                <div className="mt-4">
                  <Label>Author</Label>
                  <Input value={post.author} onChange={(e) => onChange("author", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
                </div>
                <div className="mt-3">
                  <Label>Date</Label>
                  <Input value={post.date} onChange={(e) => onChange("date", e.target.value)} placeholder="e.g. 15 June 2026" className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
                </div>
              </div>
              <div className="card-dark p-5">
                <Label>Cover Image</Label>
                {post.cover && <img src={fileUrl(post.cover)} alt="cover" className="mt-2 w-full h-32 object-cover rounded-lg border border-white/10" />}
                <Input value={post.cover} onChange={(e) => onChange("cover", e.target.value)} placeholder="Image URL" className="mt-2 bg-[#0c1530] border-white/10 text-white text-xs" />
                <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-400 cursor-pointer hover:text-amber-300">
                  <Upload className="w-3.5 h-3.5" /> Upload
                  <input type="file" accept="image/*" onChange={(e) => onUpload(e, "cover")} className="hidden" />
                </label>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seo" className="mt-5">
          <div className="card-dark p-5 space-y-4">
            <div>
              <Label>SEO Title</Label>
              <Input value={post.seo.title} onChange={(e) => onSeo("title", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              <div className="text-[11px] text-slate-500 mt-1">{(post.seo.title || "").length} / 60 chars recommended</div>
            </div>
            <div>
              <Label>Meta Description</Label>
              <Textarea rows={2} value={post.seo.description} onChange={(e) => onSeo("description", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              <div className="text-[11px] text-slate-500 mt-1">{(post.seo.description || "").length} / 160 chars recommended</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Focus Keywords</Label>
                <Input value={post.seo.keywords} onChange={(e) => onSeo("keywords", e.target.value)} placeholder="comma, separated" className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              </div>
              <div>
                <Label>Canonical URL</Label>
                <Input value={post.seo.canonical} onChange={(e) => onSeo("canonical", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label>OG Image URL</Label>
              <Input value={post.seo.ogImage} onChange={(e) => onSeo("ogImage", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={post.seo.noindex} onCheckedChange={(v) => onSeo("noindex", v)} />
              <span className="text-sm text-slate-300">No-index (hide from search engines)</span>
            </div>
            <div>
              <Label>JSON-LD Schema (optional)</Label>
              <Textarea rows={5} value={post.seo.jsonLd} onChange={(e) => onSeo("jsonLd", e.target.value)} placeholder='{"@context":"https://schema.org","@type":"Article",...}' className="mt-1.5 bg-[#0c1530] border-white/10 text-white font-mono text-xs" />
            </div>

            <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
              <div className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Google Preview</div>
              <div className="text-blue-400 text-lg leading-tight">{post.seo.title || post.title || "Page title"}</div>
              <div className="text-green-500 text-xs">{window.location.origin}/blog/{post.slug || "slug"}</div>
              <div className="text-slate-300 text-sm mt-1">{post.seo.description || post.excerpt || "Description appears here..."}</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="preview" className="mt-5">
          <div className="card-dark p-6">
            {post.cover && <img src={fileUrl(post.cover)} alt="" className="w-full h-48 object-cover rounded-xl mb-4" />}
            <h2 className="text-3xl font-bold text-white">{post.title}</h2>
            <p className="text-slate-400 mt-1 text-sm">{post.author} · {post.date}</p>
            <div className="mt-5"><MarkdownView>{post.body}</MarkdownView></div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
